import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getCreatorPrivacyNotice } from "../services/creatorApi";
import {
  getCandidateDisplayName,
  isCandidateLoggedIn,
  signOutCandidate,
} from "../utils/candidateAuth";
import CareersFooter from "../components/CareersFooter";
import { linkifyLegalText } from "../utils/linkifyLegalText";
import "./creatorLicense.scss";

export default function CreatorPrivacyPage() {
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const loggedIn = isCandidateLoggedIn();
  const displayName = getCandidateDisplayName();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCreatorPrivacyNotice()
      .then((data) => {
        if (!cancelled) {
          setNotice(data.notice || null);
          setError("");
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load the privacy notice.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="creator-license-page">
      <Helmet>
        <title>Creator Privacy Notice | QikTruck Careers</title>
        <meta
          name="description"
          content="QikTruck Creator Program privacy notice. How we process creator personal information under POPIA."
        />
      </Helmet>

      <header className="creator-license-top">
        <Link to="/creator" className="creator-license-brand">
          QikTruck Creator
        </Link>
        <button
          type="button"
          className="creator-license-menu-btn"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          Menu
        </button>
        <nav className={`creator-license-nav${menuOpen ? " is-open" : ""}`}>
          <Link to="/creator">Creator home</Link>
          <Link to="/creator/license">Content License</Link>
          {loggedIn ? (
            <>
              <Link to="/me/creator">Scripts</Link>
              <button
                type="button"
                onClick={() => {
                  signOutCandidate();
                  navigate("/creator");
                }}
              >
                Sign out{displayName ? ` (${displayName})` : ""}
              </button>
            </>
          ) : (
            <Link to={`/login?next=${encodeURIComponent("/creator/privacy")}`}>
              Sign in
            </Link>
          )}
        </nav>
      </header>

      <main className="creator-license-main">
        <p className="creator-license-kicker">Privacy</p>
        <h1>{notice?.title || "QikTruck Creator Program Privacy Notice"}</h1>
        {notice?.version ? (
          <p className="creator-license-version">Version {notice.version}</p>
        ) : null}

        {loading ? <p className="creator-license-status">Loading…</p> : null}
        {error ? <p className="creator-license-error">{error}</p> : null}

        {!loading && notice ? (
          <>
            <div className="creator-license-body">
              {(notice.sections || []).map((section) => (
                <section key={section.id} className="creator-license-section">
                  <h2>{section.title}</h2>
                  {(section.body || []).map((paragraph, index) => (
                    <p key={`${section.id}-${index}`}>
                      {linkifyLegalText(paragraph)}
                    </p>
                  ))}
                </section>
              ))}
            </div>
            <p className="creator-license-signin">
              <Link to="/creator/license">Read the Creator Content License</Link>
            </p>
          </>
        ) : null}
      </main>

      <CareersFooter />
    </div>
  );
}
